import * as React from 'react';

import {
  LucideArrowDown,
  LucideArrowUp,
  LucideDelete,
  LucideX,
} from 'lucide-react';
import { createPortal } from 'react-dom';

import { cn } from '../../../lib/stylingUtils';

export type VirtualNumericKeyboardExtraKey = {
  label: string;
  className?: string;
  onPress: () => void;
  shouldCloseKeyboardOnPress?: boolean;
};

interface VirtualKeyboardProps {
  children: React.ReactNode;
  enabled?: boolean;
  onChange?: (value: number) => void;
  value?: number | string;
  onFocus?: () => void;
  onKeyPress?: (
    key: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown',
  ) => void;
  showArrows?: boolean;
  maxDecimalPlaces?: number;
  extraKey?: VirtualNumericKeyboardExtraKey;
}

export interface VirtualNumericKeyboardRef {
  dismiss: () => void;
  open: () => void;
}

const NUMERIC_KEYS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
  '0',
  '00',
];

const KEYBOARD_HEIGHT = 300;
const KEYBOARD_WIDTH = 330;

const KeyboardButton = ({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={cn(
      '@KeyboardButton',
      cn(
        'flex h-full items-center justify-center rounded-sm border-none !bg-white/20 text-xl font-semibold !text-white',
        className,
      ),
    )}
    {...props}
  />
);

export const VirtualNumericKeyboard = React.forwardRef<
  VirtualNumericKeyboardRef,
  VirtualKeyboardProps
>(
  (
    {
      children,
      enabled,
      onChange,
      value,
      onFocus,
      onKeyPress,
      showArrows = true,
      maxDecimalPlaces,
      extraKey,
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const numpadContainerRef = React.useRef<HTMLDivElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const updatePosition = React.useCallback(() => {
      let target = wrapperRef.current as HTMLElement;

      while (target && getComputedStyle(target).display === 'contents') {
        target = target.firstElementChild as HTMLElement;
      }

      if (target) {
        const rect = target.getBoundingClientRect();

        const spaceBelow = window.innerHeight - rect.bottom;
        const showBelow = spaceBelow >= KEYBOARD_HEIGHT;
        const showRight = window.innerWidth - rect.left <= KEYBOARD_WIDTH;

        setPosition({
          ...(showRight
            ? { left: rect.right - KEYBOARD_WIDTH }
            : { left: rect.left }),
          top: showBelow ? rect.bottom + 12 : rect.top - KEYBOARD_HEIGHT - 5,
        });
      }
    }, []);

    React.useImperativeHandle(ref, () => ({
      dismiss: () => setIsVisible(false),
      open: () => {
        updatePosition();
        setIsVisible(true);
      },
    }));

    const handleFocusIn = React.useCallback(() => {
      if (!enabled) return;

      setTimeout(() => {
        try {
          (document.activeElement as HTMLInputElement).setSelectionRange(
            999,
            1000,
          );
        } catch {
          // ignore
        }
      });

      onFocus?.();
      updatePosition();

      setTimeout(() => {
        updatePosition();
      }, 100);

      setIsVisible(true);
    }, [enabled, onFocus, updatePosition]);

    const handleKeyPress = (keyValue: string) => {
      const currentValue = String(value ?? '');

      if (keyValue === 'backspace') {
        onChange?.((currentValue.slice(0, -1) || undefined) as never);
        return;
      }
      if (keyValue === 'clear') {
        onChange?.(undefined as never);
        return;
      }

      if (keyValue === '.' && currentValue.includes('.')) return;

      if (currentValue.includes('.') && maxDecimalPlaces) {
        const [, decimals] = (currentValue + keyValue).split('.');
        if (decimals?.length > maxDecimalPlaces) return;
      }

      const newValue =
        currentValue === '0' && keyValue !== '.'
          ? keyValue
          : currentValue + keyValue;

      onChange?.(newValue as never);
    };

    React.useEffect(() => {
      const handleOutsideClick = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node) &&
          numpadContainerRef.current &&
          !numpadContainerRef.current.contains(event.target as Node)
        ) {
          setIsVisible(false);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }, []);

    /*
     * Open keyboard when focused
     */
    React.useLayoutEffect(() => {
      wrapperRef.current?.addEventListener('focusin', handleFocusIn);

      return () => {
        wrapperRef.current?.removeEventListener('focusin', handleFocusIn);
      };
    }, [handleFocusIn]);

    /*
     * Dismiss keyboard when focus is lost
     */
    React.useEffect(() => {
      const listener = () => {
        if (!wrapperRef.current?.contains(document.activeElement)) {
          setIsVisible(false);
        }
      };

      document.addEventListener('focusin', listener);

      return () => {
        document.removeEventListener('focusin', listener);
      };
    }, []);

    return enabled ? (
      <>
        <div
          ref={wrapperRef}
          onKeyDown={() => setIsVisible(false)}
          className="@VirtualNumericKeyboardWrapper contents"
        >
          {children}
        </div>
        {isVisible &&
          createPortal(
            <div
              ref={numpadContainerRef}
              className="fixed z-50 flex gap-1 rounded-lg bg-neutral p-3"
              style={{
                top: position.top,
                left: position.left,
                height: KEYBOARD_HEIGHT,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onPointerDownCapture={(e) => e.stopPropagation()}
            >
              <div
                className={cn(
                  'grid grid-cols-[repeat(3,70px)] grid-rows-5 gap-1',
                  extraKey && 'grid-rows-6',
                )}
              >
                {extraKey && (
                  <KeyboardButton
                    className={cn('col-span-3 text-base', extraKey.className)}
                    onClick={() => {
                      extraKey.onPress();
                      if (extraKey.shouldCloseKeyboardOnPress) {
                        setIsVisible(false);
                      }
                    }}
                  >
                    {extraKey.label}
                  </KeyboardButton>
                )}
                <KeyboardButton
                  className="col-span-2 text-base"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleKeyPress('clear');
                  }}
                >
                  Clear
                </KeyboardButton>
                <KeyboardButton
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleKeyPress('backspace');
                  }}
                >
                  <LucideDelete className="size-5" />
                </KeyboardButton>
                {NUMERIC_KEYS.map((key) =>
                  key === '.' && maxDecimalPlaces === 0 ? (
                    <div key={key} />
                  ) : (
                    <KeyboardButton
                      key={key}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleKeyPress(key);
                      }}
                    >
                      {key}
                    </KeyboardButton>
                  ),
                )}
              </div>
              <div
                className={cn(
                  'grid w-[70px] grid-rows-5 gap-1',
                  extraKey && 'grid-rows-6',
                )}
              >
                {/* CLOSE */}
                <KeyboardButton
                  className="!bg-transparent"
                  onClick={() => setIsVisible(false)}
                >
                  <LucideX className="size-5" />
                </KeyboardButton>

                {extraKey && <div />}

                {/* ARROW UP */}
                {showArrows && (
                  <KeyboardButton
                    className="row-span-2"
                    onClick={() => onKeyPress?.('ArrowUp')}
                  >
                    <LucideArrowUp className="size-5" />
                  </KeyboardButton>
                )}

                {/* ARROW DOWN */}
                {showArrows && (
                  <KeyboardButton
                    className="row-span-2"
                    onClick={() => onKeyPress?.('ArrowDown')}
                  >
                    <LucideArrowDown className="size-5" />
                  </KeyboardButton>
                )}
              </div>
            </div>,
            document.body,
          )}
      </>
    ) : (
      children
    );
  },
);
